import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useMediaQuery} from "@mantine/hooks";
import {useNavigate} from "@tanstack/react-router";
import {getEventContainerAddEventItems} from "~/components/eventContainerAddEventButton";
import {JourneyProfileAsideCreateSurface} from "~/components/entityProfile/journeyProfileAsideCreateSurface";
import {
    JourneyProfileAsideEditSurface,
    JourneyProfileEngagementChrome,
} from "~/components/entityProfile/journeyProfileEngagementChrome";
import {resolveJourneyProfileFooterMoreActions} from "~/components/entityProfile/journeyProfileFooterMoreSurface";
import {
    filterJourneyProfileTabsForShell,
    filterProfileTabsForKind,
    resolveJourneyEntityProfileDefinition,
} from "~/components/entityProfile/resolveEntityProfileDefinition";
import {useJourneyProfileSummaryConfig} from "~/components/entityProfile/resolveJourneyProfileSummaryFields";
import type {JourneyProfileTabContext} from "~/components/entityProfile/profileTabRegistry";
import type {
    EntityProfileControllerResult,
    EntityProfileFooterActionId,
    JourneyProfileFooterMoreActionId,
    ProfileTabId,
    ProfileTabLeaveGuardRef,
} from "~/components/entityProfile/types";
import {JourneyMoreActionsButton} from "~/components/journeyProfileActionButtons";
import {navigateToScheduleCallRoute} from "~/components/scheduleCall/navigateToScheduleCallRoute";
import {useScheduleCallEngagement} from "~/components/scheduleCall/useScheduleCallEngagement";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {resolveWorkspaceLogCallRoute} from "~/domainModels/scheduleCall/resolveWorkspaceLogCallRoute";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {isEventContainerEngagementRestrictedByGlobalConstraints} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import {getScheduleCallDisabledReason} from "~/domainModels/workspaceModules/eventContainerScheduleCallDisable";
import {resolveEventContainerEditPanelGlobalConstraints, resolveEventContainerScheduleCallDisableConfig} from "~/utilities/eventContainerGlobalConstraints";
import type {EventContainerProfileSearch} from "~/utilities/eventContainerProfileNavigation";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";
import {invalidateConfigDrivenWorkspaceQueries} from "~/workspaceConfig/invalidateConfigDrivenWorkspaceQueries";
import {findConfigJourneyProfilePageId} from "~/workspaceConfig/configPageIds";
import {hasWorkspaceUiConfig, loadOrCreateWorkspaceConfig} from "~/workspaceConfig/configRuntime";
import {
    isJourneyProfilePage,
    type ColumnMapping,
    type Form,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";

export function useJourneyEntityProfileController(args: {
    journey: EventContainerWithEvents;
    search: EventContainerProfileSearch;
    /** Aside profile must not open edit via a nested aside portal (same portal slot). */
    presentation?: "page" | "aside";
    /** Dismiss the host surface (aside) before navigating away, e.g. log a call. */
    onRequestClose?: () => void;
}): {
    controller: EntityProfileControllerResult;
    tabContext: JourneyProfileTabContext;
    notesLeaveModal: ReactNode;
    openArchiveModal: () => void;
} {
    const presentation = args.presentation ?? "page";
    const onRequestClose = args.onRequestClose;
    const isMobile = useCheckMobile();
    const isCompactProfileLayout = useMediaQuery("(max-width: 1023px)", false, {
        getInitialValueInEffect: true,
    });
    const {workspace, workspaceCapabilities} = useWorkspace();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const eventTemplates = useEventTemplates();
    const eventContainerTemplate = useRequiredEventContainerTemplate(args.journey.templateId);
    const eventContainerConfiguration = eventContainerTemplate.configuration;
    const summaryViewForm = useMemo((): {viewForm: Form | undefined; columnMapping: ColumnMapping | undefined} => {
        if (hasWorkspaceUiConfig(workspace.id) !== true) {
            return {viewForm: undefined, columnMapping: undefined};
        }
        const config = loadOrCreateWorkspaceConfig(workspace.id);
        const pageId = findConfigJourneyProfilePageId(workspace.id, args.journey.templateId);
        if (pageId === undefined) {
            return {viewForm: undefined, columnMapping: undefined};
        }
        const page = config.pages[pageId];
        if (page === undefined || isJourneyProfilePage(page) !== true) {
            return {viewForm: undefined, columnMapping: undefined};
        }
        return {
            viewForm: config.forms[page.profilePanel.viewFormId],
            columnMapping: config.columnMapping,
        };
    }, [args.journey.templateId, workspace.id]);

    const [journey, setJourney] = useState(args.journey);
    const [notesDirty, setNotesDirty] = useState(false);
    const [notesComposerKey, setNotesComposerKey] = useState(0);
    const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
    const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
    const [isMorePanelOpen, setIsMorePanelOpen] = useState(false);
    const [isArchiveModalOpened, setIsArchiveModalOpened] = useState(false);
    const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
    const [archiveDeleteLoading, setArchiveDeleteLoading] = useState(false);
    const leaveGuardRef = useRef<ProfileTabLeaveGuardRef | null>(null);
    const pendingTabIdRef = useRef<ProfileTabId | null>(null);

    useEffect(() => {
        setJourney(args.journey);
    }, [args.journey]);

    const journeyMutationGlobalConstraints = useMemo(
        () =>
            resolveEventContainerEditPanelGlobalConstraints({
                workspace,
                workspaceCapabilities,
                editPanel: eventContainerConfiguration.eventContainersPage.editPanel,
            }),
        [workspace, workspaceCapabilities, eventContainerConfiguration.eventContainersPage.editPanel],
    );

    const areJourneyMutationsRestricted =
        isEventContainerEngagementRestrictedByGlobalConstraints(journey, journeyMutationGlobalConstraints) === true;

    const showArchiveAction = isAmaronWorkspace(workspaceCapabilities) === true;
    const {workspaceUser} = useWorkspaceUser();

    const createEventItems = useMemo(
        () =>
            getEventContainerAddEventItems({
                workspace,
                workspaceCapabilities,
                eventContainerId: journey.id,
                userTemplateId: workspaceUser.templateId,
            }),
        [journey.id, workspace, workspaceCapabilities, workspaceUser.templateId],
    );

    const logCallRoute = useMemo(
        () => resolveWorkspaceLogCallRoute(workspace, workspaceCapabilities),
        [workspace, workspaceCapabilities],
    );

    const scheduleCallDisableConfig = useMemo(
        () =>
            resolveEventContainerScheduleCallDisableConfig({
                workspace,
                workspaceCapabilities,
                eventContainersPage: eventContainerConfiguration.eventContainersPage,
                eventContainerDetailsPage: eventContainerConfiguration.eventContainerDetailsPage,
                context: "profile",
            }),
        [eventContainerConfiguration.eventContainerDetailsPage, eventContainerConfiguration.eventContainersPage, workspace, workspaceCapabilities],
    );

    const scheduleCall = useScheduleCallEngagement({
        navigate,
        workspaceId: workspace.id,
        scheduleCallDisableConfig,
        onSuccess: () => {
            void invalidateConfigDrivenWorkspaceQueries({
                queryClient,
                workspaceId: workspace.id,
            });
        },
    });

    const footerMoreActionItems = useMemo(
        () =>
            resolveJourneyProfileFooterMoreActions({
                showArchive: showArchiveAction,
            }),
        [showArchiveAction],
    );

    const summary = useJourneyProfileSummaryConfig({
        journey: journey,
        eventContainerTemplate: eventContainerTemplate,
        viewForm: summaryViewForm.viewForm,
        columnMapping: summaryViewForm.columnMapping,
    });

    const definitionBase = useMemo(
        () =>
            resolveJourneyEntityProfileDefinition({
                journey,
                eventContainerTemplate,
                eventTemplates,
                workspaceId: workspace.id,
                search: args.search,
                isAmaronWorkspace: isAmaronWorkspace(workspaceCapabilities) === true,
                summary,
            }),
        [args.search, eventContainerTemplate, eventTemplates, journey, summary, workspace.id, workspaceCapabilities],
    );

    const definition = useMemo(() => {
        const footerActionIds: Array<EntityProfileFooterActionId> = showArchiveAction
            ? ["logCall", "edit", "scheduleCall", "more"]
            : ["logCall", "edit", "create", "more"];

        return {
            ...definitionBase,
            tabs: filterJourneyProfileTabsForShell({
                tabs: filterProfileTabsForKind(definitionBase),
                presentation,
                isMobile: isCompactProfileLayout,
            }),
            footerActionIds,
        };
    }, [definitionBase, isCompactProfileLayout, presentation, showArchiveAction]);

    const [selectedTabId, setSelectedTabId] = useState<ProfileTabId>(definition.defaultTabId);

    useEffect(() => {
        if (definition.tabs.some((tab) => tab.id === selectedTabId) !== true) {
            setSelectedTabId(definition.defaultTabId);
        }
    }, [definition.defaultTabId, definition.tabs, selectedTabId]);

    const {requestClose: requestNotesLeave, modal: notesLeaveModal} = useUnsavedChangesGuard({
        isDirty: notesDirty,
        onDiscard: () => {
            setNotesDirty(false);
            setNotesComposerKey((current) => current + 1);
            const pendingTabId = pendingTabIdRef.current;
            pendingTabIdRef.current = null;
            if (pendingTabId !== null) {
                setSelectedTabId(pendingTabId);
            }
        },
    });

    const onTabChange = useCallback(
        (nextTabId: ProfileTabId) => {
            if (nextTabId === selectedTabId) {
                return;
            }

            const currentTab = definition.tabs.find((tab) => tab.id === selectedTabId);
            if (currentTab?.requiresLeaveGuard === true && notesDirty === true) {
                pendingTabIdRef.current = nextTabId;
                requestNotesLeave();
                return;
            }

            setSelectedTabId(nextTabId);
        },
        [definition.tabs, notesDirty, requestNotesLeave, selectedTabId],
    );

    useEffect(() => {
        leaveGuardRef.current = {
            requestLeave: (performLeave) => {
                if (notesDirty === true) {
                    requestNotesLeave();
                    return;
                }
                performLeave();
            },
        };
    }, [notesDirty, requestNotesLeave]);

    const onJourneyPatched = useCallback((patchedJourney: OttopilotEventContainer) => {
        setJourney((previous) => ({
            ...previous,
            ...patchedJourney,
        }));
    }, []);

    const onCopyContact = useCallback((value: string) => {
        void navigator.clipboard.writeText(value);
    }, []);

    const onMoreActionSelect = useCallback(
        (actionId: JourneyProfileFooterMoreActionId) => {
            switch (actionId) {
                case "notes": {
                    onTabChange("notes");
                    return;
                }
                case "archive": {
                    setIsArchiveModalOpened(true);
                    return;
                }
            }
        },
        [onTabChange],
    );

    const dismissEventRowEngagementRef = useRef<() => void>(() => {});

    const dismissFooterEngagementPanels = useCallback(() => {
        setIsEditPanelOpen(false);
        setIsCreatePanelOpen(false);
        setIsMorePanelOpen(false);
    }, []);

    const dismissEventRowEngagement = useCallback(() => {
        dismissEventRowEngagementRef.current();
    }, []);

    const registerDismissEventRowEngagement = useCallback((dismiss: () => void) => {
        dismissEventRowEngagementRef.current = dismiss;
    }, []);

    const onFooterAction = useCallback(
        (actionId: EntityProfileFooterActionId) => {
            switch (actionId) {
                case "logCall": {
                    const route = logCallRoute.trim();
                    if (route !== "") {
                        // Log a call is a full page; drop the profile surface so it isn't left on top.
                        onRequestClose?.();
                        navigateToScheduleCallRoute(navigate, route, workspace.id, journey.id);
                    }
                    return;
                }
                case "edit": {
                    dismissEventRowEngagement();
                    setIsCreatePanelOpen(false);
                    setIsMorePanelOpen(false);
                    setIsEditPanelOpen(true);
                    return;
                }
                case "create": {
                    dismissEventRowEngagement();
                    setIsEditPanelOpen(false);
                    setIsMorePanelOpen(false);
                    setIsCreatePanelOpen(true);
                    return;
                }
                case "scheduleCall": {
                    dismissEventRowEngagement();
                    setIsEditPanelOpen(false);
                    setIsCreatePanelOpen(false);
                    setIsMorePanelOpen(false);
                    if (
                        getScheduleCallDisabledReason(journey, scheduleCallDisableConfig) === null &&
                        scheduleCall.effectiveScheduleRoute.trim() !== ""
                    ) {
                        onRequestClose?.();
                    }
                    scheduleCall.openScheduleCall(journey);
                    return;
                }
                case "more": {
                    dismissEventRowEngagement();
                    setIsEditPanelOpen(false);
                    setIsCreatePanelOpen(false);
                    setIsMorePanelOpen(true);
                    return;
                }
            }
        },
        [
            dismissEventRowEngagement,
            journey,
            logCallRoute,
            navigate,
            onRequestClose,
            scheduleCall,
            scheduleCallDisableConfig,
            workspace.id,
        ],
    );

    const footerDisabledActionIds = useMemo(() => {
        const disabled: Array<EntityProfileFooterActionId> = [];
        if (areJourneyMutationsRestricted === true) {
            disabled.push("logCall", "edit", "create", "scheduleCall");
        }
        if (logCallRoute.trim() === "") {
            disabled.push("logCall");
        }
        if (createEventItems.length === 0) {
            disabled.push("create");
        }
        if (
            scheduleCall.hasScheduleAction !== true ||
            getScheduleCallDisabledReason(journey, scheduleCallDisableConfig) !== null
        ) {
            if (disabled.includes("scheduleCall") !== true) {
                disabled.push("scheduleCall");
            }
        }
        if (footerMoreActionItems.length === 0) {
            disabled.push("more");
        }
        return disabled;
    }, [
        areJourneyMutationsRestricted,
        createEventItems.length,
        footerMoreActionItems.length,
        journey,
        logCallRoute,
        scheduleCall.hasScheduleAction,
        scheduleCallDisableConfig,
    ]);

    const tabContext = useMemo(
        (): JourneyProfileTabContext => ({
            journey,
            notes: {
                journey,
                notesEventContainerSourceLabel:
                    eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase,
                composerKey: notesComposerKey,
                onJourneyPatched,
                onDirtyChange: setNotesDirty,
            },
            detailsFields: summary.fields,
            onCopyContact,
            dismissFooterEngagementPanels,
            registerDismissEventRowEngagement,
        }),
        [
            dismissFooterEngagementPanels,
            eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase,
            journey,
            notesComposerKey,
            onCopyContact,
            onJourneyPatched,
            registerDismissEventRowEngagement,
            summary.fields,
        ],
    );

    const controller = useMemo(
        (): EntityProfileControllerResult => ({
            definition,
            entity: journey,
            tabRenderProps: {
                kind: "journey",
                entity: journey,
                leaveGuardRef,
            },
            selectedTabId,
            onTabChange,
            // Outside the right card — same row as the page title (Figma: more only).
            headerChrome: (
                <div className="flex shrink-0 w-max items-center gap-2">
                    <JourneyMoreActionsButton
                        showArchive={showArchiveAction}
                        onArchive={() => {
                            setIsArchiveModalOpened(true);
                        }}
                        onDelete={() => {
                            setIsDeleteModalOpened(true);
                        }}
                    />
                </div>
            ),
            asideChrome: (
                <JourneyProfileEngagementChrome
                    journey={journey}
                    presentation={presentation}
                    isEditPanelOpen={isEditPanelOpen}
                    onCloseEditPanel={() => {
                        setIsEditPanelOpen(false);
                    }}
                    isCreatePanelOpen={isCreatePanelOpen}
                    onCloseCreatePanel={() => {
                        setIsCreatePanelOpen(false);
                    }}
                    createEventItems={createEventItems}
                    isMorePanelOpen={isMorePanelOpen}
                    onCloseMorePanel={() => {
                        setIsMorePanelOpen(false);
                    }}
                    moreActionItems={footerMoreActionItems}
                    onMoreActionSelect={onMoreActionSelect}
                    scheduleCallModal={scheduleCall.scheduleCallModal}
                    isArchiveModalOpened={isArchiveModalOpened}
                    onArchiveModalOpenChange={setIsArchiveModalOpened}
                    isDeleteModalOpened={isDeleteModalOpened}
                    onDeleteModalOpenChange={setIsDeleteModalOpened}
                    archiveDeleteLoading={archiveDeleteLoading}
                    onArchiveDeleteLoadingChange={setArchiveDeleteLoading}
                    showArchiveAction={showArchiveAction}
                />
            ),
            // Mobile keeps its native patterns (bottom sheet / full-height modal) via `asideChrome`.
            asideExclusiveSurface:
                isMobile === true ? null : presentation === "aside" && isEditPanelOpen === true ? (
                    <JourneyProfileAsideEditSurface
                        journey={journey}
                        onClose={() => {
                            setIsEditPanelOpen(false);
                        }}
                    />
                ) : presentation === "aside" && isCreatePanelOpen === true ? (
                    <JourneyProfileAsideCreateSurface
                        items={createEventItems}
                        onClose={() => {
                            setIsCreatePanelOpen(false);
                        }}
                    />
                ) : null,
            footerDisabledActionIds,
            onFooterAction,
            onCopyContact,
        }),
        [
            archiveDeleteLoading,
            createEventItems,
            definition,
            footerDisabledActionIds,
            footerMoreActionItems,
            isArchiveModalOpened,
            isCreatePanelOpen,
            isDeleteModalOpened,
            isEditPanelOpen,
            isMobile,
            isMorePanelOpen,
            journey,
            onCopyContact,
            onFooterAction,
            onMoreActionSelect,
            onTabChange,
            presentation,
            scheduleCall.scheduleCallModal,
            selectedTabId,
            showArchiveAction,
        ],
    );

    return {
        controller,
        tabContext,
        notesLeaveModal,
        openArchiveModal: () => {
            setIsArchiveModalOpened(true);
        },
    };
}
